import axios from "axios";
import Footer from "../../Footer"
import Navbar from "../../Navbar"
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import FoodCosting from "./FoodCosting";
import BeverageCosting from "./BeverageCosting";
import ActualFoodCosting from "./ActualFoodCosting";
import ActualBeverageCosting from "./ActualBeverageCosting";

const CostingAnalysis = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem('token');
    const location = useLocation();

    const { state } = location;
    const id = state?.snapshot_id;

    const [costingAnalysisData, setCostingAnalysisData] = useState({ data: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const foodData = costingAnalysisData.data?.find(item => item.item_type === "Food") || {};
    const beverageData = costingAnalysisData.data?.find(item => item.item_type === "Beverage") || {};

    const fetchCostingAnalysis = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${baseUrl}/food-and-costing-analysis`, {
                token: token,
                snapshot_id: id
            });

            const data = response.data || { data: [] };
            setCostingAnalysisData(data);
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Failed to fetch food and costing analysis data";
            toast.error(errorMsg);
            setError(errorMsg);
            console.log("Costing Analysis Error", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!id) {
            setError("No snapshot ID provided");
            return;
        }

        fetchCostingAnalysis();
    }, [token, id]);

    const calculatePercentage = (part, total) => {
        if (!part || !total || total === 0) return "0";
        const percentage = (parseFloat(part) / parseFloat(total)) * 100;
        return percentage.toFixed(2);
    };

    const formatNumber = (num) => {
        const parsed = parseFloat(num);
        return parsed.toString();
    }

    const beverageItemsSold = beverageData?.sales_summary?.items_sold || [];

    const missingBeverageItemsCount = beverageItemsSold.filter(item => !item.unit_cost).length;

    const totalBeverageItems = beverageItemsSold.length;
    const beverageAccuracy =
        totalBeverageItems > 0
            ? (((totalBeverageItems - missingBeverageItemsCount) / totalBeverageItems) * 100).toFixed(2)
            : 0;

    const foodItemsSold = foodData?.sales_summary?.items_sold || [];

    const missingFoodItemsCount = foodItemsSold.filter(item => !item.unit_cost).length;

    const totalFoodItems = foodItemsSold.length;
    const foodAccuracy =
        totalFoodItems > 0
            ? (((totalFoodItems - missingFoodItemsCount) / totalFoodItems) * 100).toFixed(2)
            : 0;

    const actualFoodAccuracy = calculatePercentage(foodData?.summary?.total_actual_consumption_cost, foodData?.sales_summary?.net_sale);

    const actualBeverageAccuracy = calculatePercentage(beverageData?.summary?.total_actual_consumption_cost, beverageData?.sales_summary?.net_sale);

    const foodCostAccuracy = actualFoodAccuracy / foodData.sales_summary?.gross_cost_percent * 100
    const beverageCostAccuracy = actualBeverageAccuracy / beverageData.sales_summary?.gross_cost_percent * 100

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-grow">
                    <div className="text-center py-10">
                        <svg className="animate-spin h-5 w-5 mr-2 inline-block text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                        </svg>
                        Loading data...
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-grow flex items-center justify-center">
                    <div className="text-red-500 text-lg">{error}</div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />

            <div className="p-4 text-sm bg-white text-black">
                <h1 className="text-lg font-semibold mb-6 text-center">
                    Food and Costing Analysis for {costingAnalysisData.outlet_name} <br />
                    <span className="text-base font-normal">
                        From <span className="font-semibold">{costingAnalysisData.start_date}</span> To <span className="font-semibold">{costingAnalysisData.end_date}</span>
                    </span>
                </h1>

                <div className="grid grid-cols-2 gap-4">
                    <FoodCosting
                        foodData={foodData}
                        calculatePercentage={calculatePercentage}
                        missingFoodItemsCount={missingFoodItemsCount}
                        totalFoodItems={totalFoodItems}
                        foodAccuracy={foodAccuracy}
                    />

                    <BeverageCosting
                        beverageData={beverageData}
                        calculatePercentage={calculatePercentage}
                        missingBeverageItemsCount={missingBeverageItemsCount}
                        totalBeverageItems={totalBeverageItems}
                        beverageAccuracy={beverageAccuracy}
                    />
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                    <ActualFoodCosting
                        foodData={foodData}
                        calculatePercentage={calculatePercentage}
                        costAccuracy={foodCostAccuracy}
                        actualAccuracy={actualFoodAccuracy}
                        formatNumber={formatNumber}
                    />

                    <ActualBeverageCosting
                        beverageData={beverageData}
                        calculatePercentage={calculatePercentage}
                        costAccuracy={beverageCostAccuracy}
                        actualAccuracy={actualBeverageAccuracy}
                        formatNumber={formatNumber}
                    />
                </div>
            </div>

            <Footer />
        </div>
    )
}

export default CostingAnalysis